import * as CHQ from "chq";

// ========================================================================== //
// utilities
// ========================================================================== //

export const mockRichText = (text: string) => JSON.stringify({
  blocks: [{
    key: "00000",
    text,
    type: "unstyled",
    depth: 0,
    inlineStyleRanges: [],
    entityRanges: [],
    data: {}
  }],
  entityMap: {}
});

const makeFactory = <T extends object>(defaults: T) => (opts: Partial<T> = {}): T => ({
  ...defaults, ...opts
});

// ========================================================================== //
// model mocks
// ========================================================================== //

export const mockAnnouncement = makeFactory<CHQ.Announcement>({
  id: 1,
  active: true,
  body: mockRichText("Mock announcement"),
  createdAt: "2019-01-01",
  image: {
    thumbUrl: "http://via.placeholder.com/100x100"
  },
  position: 1,
  title: "Mock announcement title"
});

export const mockLocation = makeFactory<CHQ.Location>({
  id: 1,
  name: "Mock location"
});


export const mockDegree = makeFactory<CHQ.Degree>({
  id: 1,
  name: "Mock degree"
});

export const mockClassYear = makeFactory<CHQ.ClassYear>({
  id: 1,
  name: "Mock class year"
});

export const mockUser = makeFactory<CHQ.User>({
  id: 1,
  active: true,
  claimed: true,
  culturehqAdmin: false,
  email: "mockuser@example.com",
  title: "Mock Title",
  location: mockLocation(),
  name: "Mock user",
  organizationAdmin: false,
  avatar: {
    thumbUrl: "http://via.placeholder.com/100x100",
    mediumUrl: "http://via.placeholder.com/500x500"
  },
  interests: [],
  skills: [],
  companies: [],
  departments: [],
  clubs: [],
  programs: [],
  degree: mockDegree(),
  classYear: mockClassYear(),
  points: 0,
  role: "regular"
});

export const mockMedia = makeFactory<CHQ.Media>({
  url: "https://culturehq-direct-uploads.s3.us-west-2.amazonaws.com/video/2023/6/30/1688135097.mp4",
  shareableUrl: "https://culturehq-direct-uploads.s3.us-west-2.amazonaws.com/video/2023/6/30/1688135097.mp4",
  duration: 60,
  thumbnail: "https://culturehq-direct-uploads.s3.us-west-2.amazonaws.com/video/2023/6/30/1688135093_thumbnail.jpg",
  fullUrl: "https://culturehq-direct-uploads.s3.us-west-2.amazonaws.com/video/2023/6/30/1688135097.mp4",
  smallUrl: "https://culturehq-direct-uploads.s3.us-west-2.amazonaws.com/video/2023/6/30/1688135097.mp4",
  alt: "",
  mediaType: "video",
  subtitles: [],
  width: 1280,
  height: 720
});

export const mockMediaImage = makeFactory<CHQ.Media>({
  url: "https://culturehq-direct-uploads.s3.us-west-2.amazonaws.com/image/2023/7/7/10151/mini_magick20230707-1873-p4llhr.jpg",
  shareableUrl: "https://culturehq-direct-uploads.s3.us-west-2.amazonaws.com/image/2023/7/7/10151/mini_magick20230707-1873-p4llhr.jpg",
  thumbnail: "https://culturehq-direct-uploads.s3.us-west-2.amazonaws.com/image/2023/7/7/10151/mini_magick20230707-1873-p4llhr.jpg",
  mediaType: "image"
});

export const mockMediaVideo = makeFactory<CHQ.Media>({
  url: "https://culturehq-direct-uploads.s3.us-west-2.amazonaws.com/video/2023/10/30/1698640061.mp4",
  shareableUrl: "https://culturehq-direct-uploads.s3.us-west-2.amazonaws.com/video/2023/10/30/1698640061.mp4",
  duration: 60,
  thumbnail: "https://culturehq-direct-uploads.s3.us-west-2.amazonaws.com/video/2023/10/30/1698640060_thumbnail.jpg",
  mediaType: "video",
  width: 1280,
  height: 720
});

export const mockStoryQuestion = makeFactory<CHQ.StoryQuestion>({
  id: 1,
  externalMapperValueAssociations: [],
  question: "This a prompt",
  user: mockUser(),
  storyQuestionType: "child_prompt",
  allowedTypes: "all"
});

export const mockStory = makeFactory<CHQ.Story>({
  id: 1,
  body: "",
  bodyTags: [],
  creator: mockUser(),
  externalState: "pending",
  title: "",
  questionId: 1,
  question: mockStoryQuestion(),
  createdAt: "2019-01-01",
  media: mockMedia(),
  cheersCount: 1
});

export const mockStoryVideo = makeFactory<CHQ.Story>({
  id: 1,
  body: mockRichText("Mock post"),
  bodyTags: [],
  creator: mockUser(),
  externalState: "pending",
  title: "",
  questionId: 1,
  question: mockStoryQuestion(),
  createdAt: "2019-01-01",
  media: mockMediaVideo(),
  cheersCount: 0
});

export const mockStoryImage = makeFactory<CHQ.Story>({
  ...mockStoryVideo(),
  media: mockMediaImage()
});

export const mockCompany = makeFactory<CHQ.Company>({
  id: 1,
  name: "Mock company"
});

export const mockDepartment = makeFactory<CHQ.Department>({
  id: 1,
  name: "Mock department"
});

export const mockDepartments = [
  mockDepartment({ id: 1, name: "Defense Against The Dark Arts" }),
  mockDepartment({ id: 2, name: "Charms" }),
  mockDepartment({ id: 3, name: "Potions" })
];

export const mockRsvp = makeFactory<CHQ.Rsvp>({
  id: 1,
  user: mockUser(),
  responseType: "invited"
});

export const mockRsvpSegment = makeFactory<CHQ.RsvpSegment>({
  name: "Mock rsvp segment",
  total: 10
});

const eventCommon = {
  id: 1,
  cancelledAt: null,
  endsAt: "2019-01-01",
  host: mockUser(),
  image: {
    mediumUrl: "http://via.placeholder.com/500x500"
  },
  name: "Mock event",
  details: "These are the mock event details",
  public: true,
  startsAt: "2019-01-01"
};

export const mockEvent = makeFactory<CHQ.Event>(eventCommon);

export const mockEventWithRsvp = makeFactory<CHQ.EventWithRsvp>({
  ...eventCommon,
  rsvpForUser: mockRsvp()
});

export const mockEventAnalytics = makeFactory<CHQ.EventAnalytics>({
  name: "Mock event",
  startsAt: "2019-01-01",
  attendees: {
    confirmedOnce: 10,
    confirmedTwice: 10
  },
  rating: 5,
  impact: {
    achieved: 80,
    potential: 100
  },
  event: mockEvent()
});

export const mockEventHost = makeFactory<CHQ.EventHost>({
  hosted: 1,
  user: mockUser()
});

export const mockEventResponse = makeFactory<CHQ.EventResponse>({
  id: 1,
  body: "Mock body",
  createdAt: "2019-01-01",
  event: mockEvent(),
  sentiment: 3
});

export const mockEventType = makeFactory<CHQ.EventType>({
  total: 1,
  name: "Mock event type"
});

export const mockFeedback = makeFactory<CHQ.Feedback>({
  id: 1,
  body: "Mock feedback"
});

export const mockFlag = makeFactory<CHQ.Flag>({
  id: 1,
  flaggableType: "User",
  flaggable: mockUser()
});

export const mockInterest = makeFactory<CHQ.Interest>({
  id: 1,
  name: "Mock interest"
});

export const mockMention = makeFactory<CHQ.Mention>({
  id: 1,
  entityType: "Event",
  entity: mockEvent()
});

export const mockPost = makeFactory<CHQ.Post>({
  id: 1,
  body: mockRichText("Mock post"),
  creator: mockUser(),
  title: "Mock title"
});

export const mockSkillOrganizationField = makeFactory<CHQ.OrganizationField>({
  table: "skills",
  label: "Skill",
  pluralLabel: "Skills",
  multipleFieldLabel: "Skills"
});

export const mockOrganization = makeFactory<CHQ.Organization>({
  id: 1,
  isEducational: false,
  logo: {
    originalUrl: ""
  },
  name: "Mock organization",
  employerBranding: true,
  eventsEnabled: true,
  externalMappers: [],
  recognitionsEnabled: true,
  postsEnabled: true,
  slackIntegration: undefined,
  surveysEnabled: true,
  organizationFields: [mockSkillOrganizationField()],
  userCreatedEventsEnabled: true,
  userCreatedInvitesEnabled: true,
  userCreatedPostsEnabled: true,
  userCreatedSurveysEnabled: true,
  createYourOwnStory: true,
  storiesNeedApprovalEnabled: true,
  unsplashImages: true,
  externalMappedIds: false,
  firstNameLastNameEnabled: false,
  organizationJobMapperRoles: []
});

export const mockNonUnsplashOrganization = makeFactory<CHQ.Organization>({
  ...mockOrganization(),
  unsplashImages: false
});

export const mockOrganizationValue = makeFactory<CHQ.OrganizationValue>({
  id: 1,
  name: "Mock organization value"
});

export const mockPhoto = makeFactory<CHQ.Photo>({
  id: 1,
  caption: null,
  cheersCount: 0,
  image: {
    mediumUrl: "http://via.placeholder.com/500x500",
    largeUrl: "http://via.placeholder.com/900x900"
  },
  uploader: mockUser()
});

export const mockPhotoTag = makeFactory<CHQ.PhotoTag>({
  id: 1,
  photo: mockPhoto()
});

export const mockProfile = makeFactory<CHQ.Profile>({
  ...mockUser(),
  organization: mockOrganization(),
  organizations: [mockOrganization()]
});

export const mockRecognitionType = makeFactory<CHQ.RecognitionType>({
  id: 1,
  name: "Mock recognition type",
  image: {
    thumbUrl: "http://via.placeholder.com/100x100",
    mediumUrl: "http://via.placeholder.com/500x500"
  }
});

export const mockRecognition = makeFactory<CHQ.Recognition>({
  id: 1,
  approved: true,
  body: mockRichText("Mock recognition"),
  cheersCount: 0,
  creator: mockUser(),
  users: [mockUser({ id: 2 })],
  organizationValues: [],
  recognitionType: mockRecognitionType()
});

export const mockSkill = makeFactory<CHQ.Skill>({
  id: 1,
  name: "Mock skill"
});

export const mockSlackIntegration = makeFactory<CHQ.SlackIntegration>({
  id: 1,
  channel: "#culturehq",
  slackDefaultChannels: []
});

const surveyCommon = {
  id: 1,
  active: true,
  creator: mockUser(),
  responseCount: 5,
  viewableResults: false,
  surveyItems: [],
  title: "Mock survey"
};

export const mockSurvey = makeFactory<CHQ.Survey>(surveyCommon);

export const mockSurveyWithStatus = makeFactory<CHQ.SurveyWithStatus>({
  ...surveyCommon,
  statusForUser: "complete"
});

export const mockSurveyItem = makeFactory<CHQ.SurveyItem>({
  id: 1,
  prompt: "Mock survey item",
  itemType: "free_text"
});

export const mockUserInterest = makeFactory<CHQ.UserInterest>({
  id: 1,
  user: mockUser(),
  interest: mockInterest(),
  endorsementCount: 0
});

export const mockUserSkill = makeFactory<CHQ.UserSkill>({
  id: 1,
  user: mockUser(),
  skill: mockSkill(),
  endorsementCount: 0
});

export const mockEndorsement = makeFactory<CHQ.Endorsement>({
  id: 1,
  body: mockRichText("Mock endorsement"),
  creator: mockUser(),
  userInterests: [
    mockUserInterest()
  ],
  userSkills: [
    mockUserSkill()
  ],
  createdAt: "2019-01-01"
});

// ========================================================================== //
// user activity mocks
// ========================================================================== //

const userActivityCommon = {
  id: 1,
  activityType: "post_created",
  cheersCount: 0,
  users: [mockUser()],
  entity: mockPost()
};

export const mockUserActivity = makeFactory<CHQ.UserActivity>(userActivityCommon);

export const mockUserActivityPostCreated = makeFactory<CHQ.UserActivityPostCreated>({
  ...userActivityCommon,
  activityType: "post_created",
  entity: mockPost()
});

// ========================================================================== //
// comment mocks
// ========================================================================== //

const commentCommon = {
  id: 1,
  body: mockRichText("Mock comment"),
  cheersCount: 0,
  childComments: [],
  commentable: {},
  commentableId: 1,
  commentableType: "Post",
  entity: {},
  user: mockUser()
};

export const mockComment = makeFactory<CHQ.Comment>(commentCommon);

export const mockCommentEvent = makeFactory<CHQ.CommentEvent>({
  ...commentCommon,
  commentableType: "Event"
});

export const mockCommentPhoto = makeFactory<CHQ.CommentPhoto>({
  ...commentCommon,
  commentableType: "Photo"
});

export const mockCommentPost = makeFactory<CHQ.CommentPost>({
  ...commentCommon,
  commentableType: "Post"
});

export const mockCommentRecognition = makeFactory<CHQ.CommentRecognition>({
  ...commentCommon,
  commentableType: "Recognition"
});

// ========================================================================== //
// cheer mocks
// ========================================================================== //

const cheerCommon = {
  id: 1,
  user: mockUser()
};

export const mockCheerComment = makeFactory<CHQ.CheerComment>({
  ...cheerCommon,
  cheerable: mockComment()
});

export const mockCheerPhoto = makeFactory<CHQ.CheerPhoto>({
  ...cheerCommon,
  cheerable: mockPhoto()
});

export const mockCheerRecognition = makeFactory<CHQ.CheerRecognition>({
  ...cheerCommon,
  cheerable: mockRecognition()
});

// ========================================================================== //
// notification mocks
// ========================================================================== //

const notificationCommon = {
  id: 1,
  actor: mockUser(),
  createdAt: "2019-01-01",
  message: null,
  imageUrl: undefined,
  imageAlt: undefined,
  thumbUrl: undefined,
  smallUrl: undefined,
  fullUrl: undefined
};

export const mockNotificationAutoRecognized = makeFactory<CHQ.NotificationAutoRecognized>({
  ...notificationCommon,
  entity: {
    ...mockRecognition(),
    creator: null
  },
  notificationType: "auto_recognized"
});

export const mockNotificationCommentCheered = makeFactory<CHQ.NotificationCommentCheered>({
  ...notificationCommon,
  entity: mockCheerComment(),
  notificationType: "comment_cheered"
});

export const mockNotificationCommentReplied = makeFactory<CHQ.NotificationCommentReplied>({
  ...notificationCommon,
  entity: mockComment(),
  notificationType: "comment_replied"
});

export const mockNotificationConnectionCreated = makeFactory<CHQ.NotificationConnectionCreated>({
  ...notificationCommon,
  entity: mockUser(),
  notificationType: "connection_created"
});

export const mockNotificationEndorsementCreated = makeFactory<CHQ.NotificationEndorsementCreated>({
  ...notificationCommon,
  entity: mockEndorsement(),
  notificationType: "endorsement_created"
});

export const mockNotificationEventCancelled = makeFactory<CHQ.NotificationEventCancelled>({
  ...notificationCommon,
  entity: mockEvent({ cancelledAt: "2019-01-01" }),
  notificationType: "event_cancelled",
  message: null
});

export const mockNotificationEventCommented = makeFactory<CHQ.NotificationEventCommented>({
  ...notificationCommon,
  entity: {
    ...mockCommentEvent(),
    commentable: mockEvent()
  },
  notificationType: "event_commented"
});

export const mockNotificationEventConfirmed = makeFactory<CHQ.NotificationEventConfirmed>({
  ...notificationCommon,
  entity: {
    ...mockRsvp(),
    event: mockEvent()
  },
  notificationType: "event_confirmed"
});

export const mockNotificationEventDeleted = makeFactory<CHQ.NotificationEventDeleted>({
  ...notificationCommon,
  entity: null,
  message: "Mock event",
  notificationType: "event_deleted"
});

export const mockNotificationEventHostingCapped = makeFactory<CHQ.NotificationEventHostingCapped>({
  ...notificationCommon,
  entity: {
    ...mockRsvp(),
    event: mockEvent()
  },
  notificationType: "event_hosting_capped"
});

export const mockNotificationEventHostingEnded = makeFactory<CHQ.NotificationEventHostingEnded>({
  ...notificationCommon,
  entity: mockEvent(),
  notificationType: "event_hosting_ended"
});

export const mockNotificationEventHostingRsvpd = makeFactory<CHQ.NotificationEventHostingRsvpd>({
  ...notificationCommon,
  entity: {
    ...mockRsvp(),
    event: mockEvent()
  },
  message: "accepted",
  notificationType: "event_hosting_rsvpd"
});

export const mockNotificationEventInterestsAligned = (
  makeFactory<CHQ.NotificationEventInterestsAligned>({
    ...notificationCommon,
    entity: mockEvent(),
    message: "mock interest 1, mock interest 2, and mock interest 3",
    notificationType: "event_interests_aligned"
  })
);

export const mockNotificationEventInvite = makeFactory<CHQ.NotificationEventInvite>({
  ...notificationCommon,
  entity: mockEvent(),
  notificationType: "event_invite"
});

export const mockNotificationEventNotification = makeFactory<CHQ.NotificationEventNotification>({
  ...notificationCommon,
  entity: mockEvent(),
  message: "Mock notification",
  notificationType: "event_notification"
});

export const mockNotificationFeedbackReviewed = makeFactory<CHQ.NotificationFeedbackReviewed>({
  ...notificationCommon,
  entity: mockFeedback(),
  notificationType: "feedback_reviewed"
});

export const mockNotificationFeedbackSubmitted = makeFactory<CHQ.NotificationFeedbackSubmitted>({
  ...notificationCommon,
  entity: mockFeedback(),
  notificationType: "feedback_submitted"
});

export const mockNotificationFlagCreated = makeFactory<CHQ.NotificationFlagCreated>({
  ...notificationCommon,
  entity: mockFlag(),
  notificationType: "flag_created"
});

export const mockNotificationInterestsAligned = makeFactory<CHQ.NotificationInterestsAligned>({
  ...notificationCommon,
  entity: mockUserInterest(),
  notificationType: "interests_aligned"
});

export const mockNotificationMentioned = makeFactory<CHQ.NotificationMentioned>({
  ...notificationCommon,
  entity: mockMention({
    entityType: "Event",
    entity: mockEvent()
  }),
  notificationType: "mentioned"
});

export const mockNotificationPhotoCheered = makeFactory<CHQ.NotificationPhotoCheered>({
  ...notificationCommon,
  entity: mockCheerPhoto(),
  notificationType: "photo_cheered"
});

export const mockNotificationPhotoCommented = makeFactory<CHQ.NotificationPhotoCommented>({
  ...notificationCommon,
  entity: mockCommentPhoto(),
  notificationType: "photo_commented"
});

export const mockNotificationPhotoTagged = makeFactory<CHQ.NotificationPhotoTagged>({
  ...notificationCommon,
  entity: mockPhotoTag(),
  notificationType: "photo_tagged"
});

export const mockNotificationPostCommented = makeFactory<CHQ.NotificationPostCommented>({
  ...notificationCommon,
  entity: mockCommentPost(),
  notificationType: "post_commented"
});

export const mockNotificationRecognitionApproved = (
  makeFactory<CHQ.NotificationRecognitionApproved>({
    ...notificationCommon,
    entity: mockRecognition(),
    notificationType: "recognition_approved"
  })
);

export const mockNotificationRecognitionGivenCheered = (
  makeFactory<CHQ.NotificationRecognitionGivenCheered>({
    ...notificationCommon,
    entity: mockCheerRecognition(),
    notificationType: "recognition_given_cheered"
  })
);

export const mockNotificationRecognitionGivenCommented = (
  makeFactory<CHQ.NotificationRecognitionGivenCommented>({
    ...notificationCommon,
    entity: mockCommentRecognition(),
    notificationType: "recognition_given_commented"
  })
);

export const mockNotificationRecognitionPending = (
  makeFactory<CHQ.NotificationRecognitionPending>({
    ...notificationCommon,
    entity: mockRecognition(),
    notificationType: "recognition_pending"
  })
);

export const mockNotificationRecognitionReceivedCheered = (
  makeFactory<CHQ.NotificationRecognitionReceivedCheered>({
    ...notificationCommon,
    entity: mockCheerRecognition(),
    notificationType: "recognition_received_cheered"
  })
);

export const mockNotificationRecognitionReceivedCommented = (
  makeFactory<CHQ.NotificationRecognitionReceivedCommented>({
    ...notificationCommon,
    entity: mockCommentRecognition(),
    notificationType: "recognition_received_commented"
  })
);

export const mockNotificationRecognized = makeFactory<CHQ.NotificationRecognized>({
  ...notificationCommon,
  entity: mockRecognition(),
  notificationType: "recognized"
});

export const mockAIPromptQuestions = [
  {
    question: "What do you love most about working here?",
    instructions: "instructions for What do you love most about working here"
  },
  {
    question: "What’s one thing that makes this company a great place to work?",
    instructions: "instructions for What’s one thing that makes this company a great place to work"
  },
  {
    question: "How has our company helped you grow in your career?",
    instructions: "instructions for How has our company helped you grow in your career"
  },
  {
    question: "What’s your favorite part of the company's culture?",
    instructions: "instructions for What’s your favorite part of the company's culture"
  },
  {
    question: "Why do you enjoy being part of the company's team?",
    instructions: "instructions for Why do you enjoy being part of the company's team"
  }];
